import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { UserPlus, Edit, Loader2, Users } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PartnersPage() {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({ full_name: '', email: '' });
  const [editData, setEditData] = useState({ id: '', full_name: '', email: '' });

  const fetchPartners = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/partners`);
      setPartners(data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleAddPartner = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/partners`, formData);
      setShowAddDialog(false);
      setFormData({ full_name: '', email: '' });
      fetchPartners();
      alert(`Partner created!\n\nName: ${formData.full_name}\nLogin Email: ${res.data.login_email}\nPassword: manager0100@\nRole: partner`);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating partner');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (partner) => {
    setEditData({ id: partner.id, full_name: partner.name, email: partner.email });
    setShowEditDialog(true);
  };

  const handleEditPartner = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/api/partners/${editData.id}`, {
        full_name: editData.full_name,
        email: editData.email
      });
      setShowEditDialog(false);
      fetchPartners();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error updating partner');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="partners-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Partners</h1>
            <p className="text-slate-500 mt-1">Manage referral partners</p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#1a2e5a] hover:bg-[#15254a] text-white" data-testid="add-partner-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Partner
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Partner</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddPartner} className="space-y-4">
                <div>
                  <Label htmlFor="full_name" className="font-semibold">Partner Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    placeholder="Full name"
                    data-testid="input-partner-name"
                  />
                </div>
                <div>
                  <Label htmlFor="partner_email" className="font-semibold">Partner Email (used as login) *</Label>
                  <Input
                    id="partner_email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="partner@email.com"
                    data-testid="input-partner-email"
                  />
                  <p className="text-xs text-slate-500 mt-1">This email will be used as the login. Default password: manager0100@</p>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline" type="button">Cancel</Button>
                  </DialogClose>
                  <Button type="submit" className="bg-[#1a2e5a] hover:bg-[#15254a] text-white" disabled={formLoading} data-testid="submit-partner-btn">
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    Create Partner
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Partners Table */}
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Name</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Email (Login)</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Created</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 uppercase">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : partners.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                      No partners found
                    </TableCell>
                  </TableRow>
                ) : (
                  partners.map((partner) => (
                    <TableRow key={partner.id} className="hover:bg-slate-50" data-testid={`partner-row-${partner.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#1a2e5a]/10 flex items-center justify-center">
                            <Users className="w-4 h-4 text-[#1a2e5a]" />
                          </div>
                          <span className="font-medium text-slate-900">{partner.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{partner.email}</TableCell>
                      <TableCell className="text-slate-600">
                        {partner.created_at ? format(new Date(partner.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(partner)} data-testid={`edit-partner-${partner.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Partner</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditPartner} className="space-y-4">
              <div>
                <Label htmlFor="edit_name" className="font-semibold">Partner Full Name</Label>
                <Input
                  id="edit_name"
                  value={editData.full_name}
                  onChange={(e) => setEditData({ ...editData, full_name: e.target.value })}
                  required
                  data-testid="edit-partner-name"
                />
              </div>
              <div>
                <Label htmlFor="edit_email" className="font-semibold">Partner Email (Login)</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  required
                  data-testid="edit-partner-email"
                />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" type="button">Cancel</Button>
                </DialogClose>
                <Button type="submit" className="bg-[#1a2e5a] hover:bg-[#15254a] text-white" disabled={formLoading} data-testid="save-partner-btn">
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
